import { Injectable } from '@angular/core';
import { TimeZoneConfig } from './types';
import { BehaviorSubject } from 'rxjs';
import { TimeZoneConfigService } from './time-zone.config.service';
import { DateTime } from 'luxon';

@Injectable({
  providedIn: 'root',
})
export class TimeZoneService {
  private config: TimeZoneConfig;

  private timeZoneSubject$: BehaviorSubject<string> = new BehaviorSubject('');

  constructor(private readonly timeZoneConfigService: TimeZoneConfigService) {
    this.config = this.timeZoneConfigService.get();
    this.timeZoneSubject$ = new BehaviorSubject<string>(this.config.timeZoneId);
  }

  setTimezone(timezone: string): void {
    this.timeZoneSubject$.next(timezone);
  }

  getTimezone(): string {
    return this.timeZoneSubject$.value;
  }

  fromUtcToLocal(
    utcDate: Date | string | DateTime,
    format: string = 'dd-MM-yyyy HH:mm',
    locale: string = 'fr-FR',
  ): string {
    if (!utcDate) return utcDate;

    const timeZoneId: string = this.getTimezone();
    const isoString = this.getIsoString(utcDate);
    return DateTime.fromISO(isoString, { zone: 'utc' })
      .setZone(timeZoneId)
      .setLocale(locale)
      .toFormat(format);
  }

  fromLocalToUtc(
    localDate: Date | string | DateTime,
    includeOffset: boolean = true,
    locale: string = 'fr-FR',
  ): string | null {
    if (!localDate) return localDate;
    const timeZoneId: string = this.getTimezone();
    const isoString = this.getIsoString(localDate);
    const localDateTime = DateTime.fromISO(isoString, {
      zone: timeZoneId,
    }).setLocale(locale);

    return localDateTime.toUTC().toISO({ includeOffset: includeOffset });
  }

  // toLastHoursOfTheDay(date: Date | string | DateTime): string {
  //   const timeZoneId: string = this.getTimezone();
  //   const isoString = this.getIsoString(date);
  //
  //   return this.fromLocalSetTime(date, 23, 59, 59);
  // }

  fromLocalSetTime(
    localDate: Date | string | DateTime,
    hours: number,
    minutes: number,
    seconds: number,
    locale: string = 'fr-FR',
  ): string | null {
    const timeZoneId: string = this.getTimezone();
    const isoString = this.getIsoString(localDate);
    const localDateTime = DateTime.fromISO(isoString, {
      zone: timeZoneId,
    })
      .setLocale(locale)
      .set({ hour: hours, minute: minutes, second: seconds });

    return localDateTime.toUTC().toISO();
  }

  private getIsoString(date: Date | string | DateTime): string {
    let isoString: string;
    if (date instanceof DateTime) {
      isoString = date.toISO()
        ? <string>date.toISO()
        : date.toJSDate().toISOString();
    } else {
      isoString = date instanceof Date ? date.toISOString() : date;
    }

    return isoString;
  }
}
